import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useDispatch } from "react-redux";

import styles from "./index.module.css";
import { render } from "../utils/render";
import {
  reviewRegistration,
  reviewConduct,
  reviewProgress,
  reviewProgressReport,
  reviewStop,
} from "../../../reducer/studentResearch/action";

// StudentProjectModal — modal đa năng cho tất cả hành động trên đề tài SV.
// Thiết kế mới: `open` là object dạng `{ <mode>: true, ...extra }` với mode
// khớp key trong `modalPropertiesDict` (xem render.js).
//
// Modal truyền `renderProps` xuống form của từng mode; form tự lo dispatch action.
// Nhờ vậy modal không cần biết chi tiết từng form — simple + ít bug hơn bản cũ.
function StudentProjectModal({ open, setOpen, year, type, detail, chosenDepartmentId }) {
  const dispatch = useDispatch();

  // State phụ cho các mode cần form state (council mới tạo).
  // CouncilForm cần values + setValues do cách nó được viết (Formik-less).
  const [councilValues, setCouncilValues] = useState({});

  const handleClose = useCallback(() => {
    setOpen(null);
    setCouncilValues({});
  }, [setOpen]);

  // Khi mở modal councilFinal, seed councilValues từ assignment đang có (nếu
  // có) hoặc khởi tạo trống.
  // Schema mới (2026-05): detail.councilAssignments[] thay vì
  // detail.councilMemberInfo (1 record duy nhất).
  // Refactor 2026-08-26: bỏ councilProgress — chỉ còn hội đồng nghiệm thu.
  useEffect(() => {
    if (!open) return;
    const desiredType = open.councilFinal ? "final" : null;
    if (!desiredType) return;
    const existing = (detail?.councilAssignments || []).find((a) => a.councilType === desiredType);
    if (existing) {
      setCouncilValues({
        type: desiredType,
        councilTemplateId: existing.councilTemplateId || 0,
        members: existing.members || [],
      });
    } else {
      setCouncilValues({ type: desiredType, councilTemplateId: 0, members: [] });
    }
  }, [open, detail]);

  const modalProperties = useMemo(() => (open ? render(open) : null), [open]);

  // Bundle các action review — truyền xuống ReviewForm qua renderProps.
  const actions = useMemo(
    () => ({
      reviewRegistration,
      reviewConduct,
      reviewProgress,
      reviewProgressReport,
      reviewStop,
    }),
    []
  );

  const renderProps = useMemo(
    () => ({
      open,
      type,
      year,
      detail,
      dispatch,
      actions,
      handleClose,
      chosenDepartmentId,
      rows: open?.rows,
      councilValues,
      setCouncilValues,
    }),
    [open, type, year, detail, dispatch, actions, handleClose, chosenDepartmentId, councilValues]
  );

  if (!open || !modalProperties) return null;

  return (
    <Dialog fullWidth open={!!open} maxWidth={modalProperties.size || "md"} onClose={handleClose}>
      <DialogTitle classes={{ root: styles["dialog-title-container"] }}>
        <Grid container alignItems="center">
          <Grid item xs={11}>
            <Typography variant="h5">
              <b>{modalProperties.title}</b>
            </Typography>
          </Grid>
          <Grid item xs={1} style={{ textAlign: "right" }}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <Divider />

      <DialogContent style={{ padding: 0 }}>
        {modalProperties.form(renderProps)}
      </DialogContent>

      <DialogActions style={{ padding: "8px 24px" }}>
        {modalProperties.actionButtons(renderProps).map((item, idx) => (
          <React.Fragment key={`btn-${idx}`}>
            {item?.closeButton ? (
              <Button variant="outlined" startIcon={<Close />} onClick={handleClose}>
                Đóng
              </Button>
            ) : item?.disabledText ? (
              <Tooltip title={item.disabledText}>
                <span>
                  <Button disabled {...item.props}>{item.content}</Button>
                </span>
              </Tooltip>
            ) : (
              <Button {...item.props}>{item.content}</Button>
            )}
          </React.Fragment>
        ))}
      </DialogActions>
    </Dialog>
  );
}

export default StudentProjectModal;
